import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Modal, Tooltip } from 'antd';
import { CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import { Bar, Line } from 'react-chartjs-2';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import { useAuth } from '../Auth/store';
import { useChartComparison } from './comparison.store';
import { applyCommonComparisonPeriod, formatComparisonDate, hasDifferentComparisonPeriods, loadComparisonChart, parseComparisonDate } from './comparison.helpers';
import { CustomDatepicker } from '../datepicker/custom.datepicker';
import { GetMeasurerUnitByBasis, GetFirstValueOfBasis } from '../ChartWindow/utils';
import { Toast } from '../Toast/Toast';
import './comparison.css';

const COMPARISON_CHART_MIN_HEIGHT = 200;
let comparisonHoveredTime = null;
const comparisonLabelTimes = new WeakMap();
const emptyComparisonLabelTimes = { byIndex: [], sorted: [] };

const getComparisonLabelTimes = (labels) => {
    if (!Array.isArray(labels))
        return emptyComparisonLabelTimes;

    const cached = comparisonLabelTimes.get(labels);
    const cacheMatchesLabels = cached
        && cached.byIndex?.length === labels.length
        && cached.firstLabel === labels[0]
        && cached.lastLabel === labels[labels.length - 1];
    if (!cacheMatchesLabels) {
        const byIndex = labels.map((label) => {
            const date = parseComparisonDate(label);
            return date ? date.valueOf() : null;
        });
        const sorted = byIndex
            .map((timestamp, index) => timestamp === null ? null : { index, timestamp })
            .filter(Boolean)
            .sort((left, right) => left.timestamp - right.timestamp);
        comparisonLabelTimes.set(labels, {
            byIndex,
            sorted,
            firstLabel: labels[0],
            lastLabel: labels[labels.length - 1]
        });
    }

    return comparisonLabelTimes.get(labels);
};

const findNearestComparisonIndex = (labels, timestamp) => {
    const times = getComparisonLabelTimes(labels).sorted;
    if (!times.length || !Number.isFinite(timestamp))
        return -1;

    let low = 0;
    let high = times.length - 1;
    while (low < high) {
        const middle = Math.floor((low + high) / 2);
        if (times[middle].timestamp < timestamp)
            low = middle + 1;
        else
            high = middle;
    }

    const right = times[low];
    const left = times[Math.max(low - 1, 0)];
    return Math.abs(left.timestamp - timestamp) <= Math.abs(right.timestamp - timestamp)
        ? left.index
        : right.index;
};

const getComparisonTimestampAt = (labels, index) => getComparisonLabelTimes(labels).byIndex[index] ?? null;

const comparisonCrosshairPlugin = {
    id: 'comparisonCrosshair',
    afterDraw(chart) {
        if (chart.options.plugins?.comparisonCrosshair?.enabled === false
            || !Number.isFinite(comparisonHoveredTime))
            return;

        const labels = chart.data.labels || [];
        const xScale = chart.scales.x;
        if (!xScale || labels.length === 0)
            return;

        const nearestIndex = findNearestComparisonIndex(labels, comparisonHoveredTime);
        if (nearestIndex < 0)
            return;

        const x = xScale.getPixelForValue(nearestIndex);
        const { top, bottom, left, right } = chart.chartArea;
        if (x < left || x > right)
            return;

        const context = chart.ctx;
        context.save();
        context.beginPath();
        context.setLineDash([]);
        context.lineWidth = 1;
        context.strokeStyle = '#1677ff';
        context.moveTo(x, top);
        context.lineTo(x, bottom);
        context.stroke();
        context.restore();
    }
};

Chart.register(annotationPlugin);

export const ComparisonModal = () => {
    const permission = useAuth((state) => state.permission);
    const userSettings = useAuth((state) => state.userSettings);
    const channels = useChartComparison((state) => state.channels);
    const isOpen = useChartComparison((state) => state.isOpen);
    const closeComparison = useChartComparison((state) => state.closeComparison);
    const clearChannels = useChartComparison((state) => state.clearChannels);
    const removeChannel = useChartComparison((state) => state.removeChannel);
    const setPeriodForAll = useChartComparison((state) => state.setPeriodForAll);
    const [chartResults, setChartResults] = useState({});
    const [draftPeriod, setDraftPeriod] = useState({ start: '', finish: '' });
    const [isCrosshairEnabled, setIsCrosshairEnabled] = useState(true);
    const chartRefs = useRef({});
    const hoverFrame = useRef(null);
    const pendingHoverTime = useRef(null);
    const pendingHoverChart = useRef(null);
    const lastHoveredChart = useRef(null);

    const hasMixedPeriods = hasDifferentComparisonPeriods(channels);
    const pickerPeriod = draftPeriod.start && draftPeriod.finish
        ? draftPeriod
        : {
            start: formatComparisonDate(parseComparisonDate(channels[0]?.start)) || '',
            finish: formatComparisonDate(parseComparisonDate(channels[0]?.finish)) || ''
        };

    useEffect(() => {
        if (isOpen && channels.length > 0) {
            setDraftPeriod({
                start: formatComparisonDate(parseComparisonDate(channels[0].start)) || channels[0].start,
                finish: formatComparisonDate(parseComparisonDate(channels[0].finish)) || channels[0].finish
            });
        }
    }, [isOpen, channels]);

    useEffect(() => {
        if (!isOpen || channels.length === 0) {
            setChartResults({});
            return undefined;
        }

        let isCurrentRequest = true;
        setChartResults(Object.fromEntries(
            channels.map((channel) => [channel.uuid, { status: 'loading' }])
        ));

        channels.forEach((channel) => {
            loadComparisonChart(channel, userSettings)
                .then((chart) => {
                    if (isCurrentRequest) {
                        setChartResults((current) => ({
                            ...current,
                            [channel.uuid]: { status: 'ready', chart }
                        }));
                    }
                })
                .catch((error) => {
                    if (isCurrentRequest) {
                        setChartResults((current) => ({
                            ...current,
                            [channel.uuid]: { status: 'error', error }
                        }));
                    }
                });
        });

        return () => {
            isCurrentRequest = false;
        };
    }, [isOpen, channels, userSettings]);

    useEffect(() => () => {
        if (hoverFrame.current !== null)
            cancelAnimationFrame(hoverFrame.current);
        comparisonHoveredTime = null;
    }, []);

    if (!permission?.permissions?.can_view_tech_data)
        return null;

    const onStartChange = (value) => {
        const start = formatComparisonDate(value);
        if (start)
            setDraftPeriod((period) => ({ ...period, start }));
    };

    const onFinishChange = (value) => {
        const finish = formatComparisonDate(value);
        if (finish)
            setDraftPeriod((period) => ({ ...period, finish }));
    };

    const onRefreshCharts = () => {
        applyCommonComparisonPeriod(
            [parseComparisonDate(draftPeriod.start), parseComparisonDate(draftPeriod.finish)],
            setPeriodForAll
        );
    };

    const updateChartHover = (chart, timestamp) => {
        if (!chart?.tooltip)
            return;

        const labels = chart.data.labels || [];
        const nearestIndex = findNearestComparisonIndex(labels, timestamp);
        if (nearestIndex < 0) {
            chart.tooltip.setActiveElements([], { x: 0, y: 0 });
            chart.update('none');
            return;
        }

        const activeElements = [];
        chart.data.datasets.forEach((_dataset, datasetIndex) => {
            if (!chart.isDatasetVisible(datasetIndex))
                return;

            const element = chart.getDatasetMeta(datasetIndex).data[nearestIndex];
            if (element && !element.skip)
                activeElements.push({ datasetIndex, index: nearestIndex });
        });

        const first = activeElements[0];
        const element = first && chart.getDatasetMeta(first.datasetIndex).data[first.index];
        const position = element?.tooltipPosition() || {
            x: chart.scales.x.getPixelForValue(nearestIndex),
            y: chart.chartArea.top
        };
        chart.tooltip.setActiveElements(activeElements, position);
        chart.update('none');
    };

    const syncComparisonHover = (timestamp, sourceChart = null) => {
        const sourceChartChanged = pendingHoverChart.current !== sourceChart;
        if (comparisonHoveredTime === timestamp
            && pendingHoverTime.current === timestamp
            && (isCrosshairEnabled || !sourceChartChanged))
            return;

        comparisonHoveredTime = timestamp;
        pendingHoverTime.current = timestamp;
        pendingHoverChart.current = sourceChart;
        if (hoverFrame.current !== null)
            return;

        hoverFrame.current = requestAnimationFrame(() => {
            hoverFrame.current = null;
            const currentTimestamp = pendingHoverTime.current;
            if (!isCrosshairEnabled) {
                if (pendingHoverChart.current)
                    updateChartHover(pendingHoverChart.current, currentTimestamp);
                return;
            }
            Object.values(chartRefs.current).forEach((chart) => updateChartHover(chart, currentTimestamp));
        });
    };

    const onChartHover = (event, activeElements, chart) => {
        lastHoveredChart.current = chart;
        const labels = chart.data.labels || [];
        const xScale = chart.scales.x;
        const { left, right } = chart.chartArea;
        const index = event.x < left || event.x > right
            ? undefined
            : Math.max(0, Math.min(Math.round(xScale.getValueForPixel(event.x)), labels.length - 1));
        const activeIndex = index ?? activeElements[0]?.index;
        const timestamp = activeIndex === undefined
            ? null
            : getComparisonTimestampAt(labels, activeIndex);
        syncComparisonHover(timestamp, chart);
    };

    const onCrosshairToggle = (event) => {
        const enabled = event.target.checked;
        setIsCrosshairEnabled(enabled);
        if (!enabled) {
            Object.values(chartRefs.current).forEach((chart) => {
                if (chart !== lastHoveredChart.current)
                    updateChartHover(chart, null);
            });
        }
    };

    return (
        <>
        <Toast />
        <Modal
            className="comparison-modal"
            centered
            title="Сравнение графиков"
            open={isOpen && channels.length > 0}
            onCancel={closeComparison}
            footer={[
                <Button key="clear" type="link" onClick={clearChannels}>
                    Очистить и закрыть
                </Button>,
                <Button key="close" onClick={closeComparison}>
                    Закрыть
                </Button>
            ]}
            width={1100}
        >
            <div className="comparison-period-control">
                <div className="comparison-period-label">
                    <strong>Общий период</strong>
                    {hasMixedPeriods && <span>Для графиков заданы разные периоды. Выберите новый период и нажмите «Обновить».</span>}
                </div>
                <div className="comparison-period-inputs">
                    <Checkbox
                        checked={isCrosshairEnabled}
                        onChange={onCrosshairToggle}
                    >
                        Синхронная подсказка
                    </Checkbox>
                    {pickerPeriod.start && pickerPeriod.finish && (
                        <CustomDatepicker
                            isCustom={false}
                            start={pickerPeriod.start}
                            finish={pickerPeriod.finish}
                            onStart={onStartChange}
                            onFinish={onFinishChange}
                        />
                    )}
                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={onRefreshCharts}
                        disabled={!parseComparisonDate(draftPeriod.start) || !parseComparisonDate(draftPeriod.finish)}
                    >
                        Обновить
                    </Button>
                </div>
            </div>

            <div className="comparison-charts-list">
                {channels.map((channel) => {
                    console.log(channel);
                    const result = chartResults[channel.uuid];
                    const chart = result?.chart;
                    const ComparisonChart = chart?.chartType === 'bar' ? Bar : Line;
                    const stationPosition = [
                        channel.station?.area,
                        channel.station?.district,
                        channel.station?.city,
                        channel.station?.locality,
                        channel.station?.monitoring_object
                    ].filter(Boolean).join(', ');
                    const basis = GetFirstValueOfBasis(channel.measurer?.type);
                    const unit = channel.measurer
                        ? GetMeasurerUnitByBasis(basis, channel.measurer, null, userSettings)
                        : null;
                    return (
                        <section className="comparison-chart-card" key={channel.uuid}>
                            <header className="comparison-chart-header">
                                <div className="comparison-chart-title">
                                    <strong>{channel.station?.name || 'Пост'}</strong>
                                    {stationPosition && <span>{stationPosition}</span>}
                                    <span>{channel.measurer?.typeName || channel.measurer?.type || 'Канал'}</span>
                                    {unit && <span>{unit}</span>}
                                </div>
                                <Tooltip title="Удалить график">
                                    <Button
                                        type="text"
                                        danger
                                        icon={<CloseOutlined />}
                                        aria-label={`Удалить ${channel.measurer?.typeName || 'график'} из сравнения`}
                                        onClick={() => removeChannel(channel.uuid)}
                                    />
                                </Tooltip>
                            </header>
                            <div
                                className="comparison-chart-canvas"
                                style={{ '--comparison-chart-min-height': `${COMPARISON_CHART_MIN_HEIGHT}px` }}
                                onMouseLeave={() => syncComparisonHover(null, chartRefs.current[channel.uuid])}
                            >
                                {(!result || result.status === 'loading') && (
                                    <div className="loading loading-wrapper comparison-chart-loading" aria-label="Загрузка графика" />
                                )}
                                {result?.status === 'error' && <div className="comparison-chart-error">Не удалось загрузить график</div>}
                                {result?.status === 'ready' && !chart?.hasData && <div className="comparison-chart-empty">Нет данных за выбранный период</div>}
                                {result?.status === 'ready' && chart?.hasData && (
                                    <ComparisonChart
                                        ref={(instance) => {
                                            if (instance)
                                                chartRefs.current[channel.uuid] = instance;
                                            else
                                                delete chartRefs.current[channel.uuid];
                                        }}
                                        plugins={[comparisonCrosshairPlugin]}
                                        data={{ labels: chart.labels, datasets: chart.datasets }}
                                        options={{
                                            ...chart.options,
                                            interaction: { mode: 'index', intersect: false, axis: 'x' },
                                            onHover: onChartHover,
                                            plugins: {
                                                ...chart.options.plugins,
                                                comparisonCrosshair: { enabled: isCrosshairEnabled }
                                            }
                                        }}
                                    />
                                )}
                            </div>
                        </section>
                    );
                })}
            </div>
        </Modal>
        </>
    );
};
